import { observer } from 'mobx-react-lite'

/**
 * This is the navigator you will modify to display the logged-in screens of your app.
 * You can use RootNavigator to also display an auth flow or other user flows.
 *
 * You'll likely spend most of your time in this file.
 */
import React, { useCallback } from 'react'
import {
  GestureResponderEvent,
  Linking,
  Platform,
  Pressable,
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from 'react-native'
import { Switch } from 'react-native-paper'
import { useThrottledCallback } from 'use-debounce'

import { GameStates, Player } from '@ppwr/poker'

import Crown from '../../assets/crown.svg'
import ArrowTopRight from '@/design-system/Icons/Arrow--top-right.svg'
import MaterialIcons from '@expo/vector-icons/MaterialIcons'
import { Button } from '../../components/ANewDesign/Button'
import { DrawerCardColors } from '../../components/ANewDesign/DrawerCardColors/DrawerCardColors'
import { DrawerHeaderItems } from '../../components/ANewDesign/DrawerHeaderItems/DrawerHeaderItems'
import { useCheatSheet } from '../../components/CheatSheet/useCheatSheet'
import { useSupportFormModal } from '../Support/useSupportFormModal'
import NotificationCheatSheet from '../../components/Notifications/NotificationsSheet'
import { useNotificationSheet } from '../../components/Notifications/useNotificationSheet'
import { DS } from '@/design-system'
import { translate } from '../../i18n'
import { broadcastChannel, useStore, type RootStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { RootStoreEnv } from '../../models/RootStoreEnv'

import { color, fontFamily } from '../../theme'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import { DrawerItem } from '../../components/ANewDesign/DrawerItem/DrawerItem'
import { useRemoteConfig } from '@/modules/remote-config'
import { useIsInGame } from '@/hooks/useIsInGame'
import { useDrawerStore } from '@/components/Drawer/Drawer'
import { useFocusEffect, useRouter, type Href } from 'expo-router'
import { useIsEmbeddedSegment } from '@/hooks/useIsEmbeddedSegment'
import { DrawerRoutes } from '@/components/Drawer/DrawerLegacyRoutes'
import { BUILD_VERSION_FORMATTED_TEXT } from '../../utils/buildVersionText'

// NOTES
// Diffs are based on these.
// Q maybe should include/be-more-like the DrawerItemsProps below
// isWeb, isAndroid
// isAdmin
// isGame
// isCommunityAccessEnabled

const logger = extendLogger(LoggerContexts.DrawerNavigator)

const isWeb = Platform.OS === 'web'
const ENABLE_NOTIFICATIONS = !isWeb
const isAndroid = Platform.OS === 'android'

// States that affect item display
interface IDrawerItemState {
  isAdmin: boolean
  isCommunityAccessEnabled: boolean
  isInGame: boolean
  isFourColoredDeckEnabled: boolean
  isSubscribed: boolean
  shouldShowMembershipButtons: boolean
  store?: RootStore
}

// Definition of each item in list of drawer items
interface IDrawerItem {
  svg?: React.ReactNode
  route: DrawerRoutes
  title: string
  subtitle?: string
  emphasize?: boolean
  sectionBreak?: boolean
  skipItem?: (item: IDrawerItem, state: IDrawerItemState) => boolean
}

const LinkArrow = () => <ArrowTopRight width="16" height="16" color="black" />

const getDrawerItems = ({
  isInGame,
  isFourColoredDeckEnabled,
  isSubscribed,
  shouldShowMembershipButtons,
  isCommunityAccessEnabled
}: IDrawerItemState): IDrawerItem[] => {
  const TopUserControls: IDrawerItem[] = [
    {
      route: DrawerRoutes.cheatSheet,
      title: translate('drawer.handRankings'),
      // skipItem: (item, state) => state.isInGame,
      svg: <DS.Icon name="Key" size={16} color="Global/Black" />
    }
  ]

  const TopGameControls: IDrawerItem[] = [
    {
      route: DrawerRoutes.leaderboard,
      title: translate('drawer.leaderboard'),
      skipItem: (item, state) =>
        state.store?.game?.defaultTable?.state.tag === GameStates.ReadyToPlay.tag &&
        state.store?.game?.isTournamentModeOn === true
    },
    {
      route: isFourColoredDeckEnabled ? DrawerRoutes.hide4ColorDeck : DrawerRoutes.show4ColorDeck,
      title: translate(isFourColoredDeckEnabled ? 'drawer.fourColorHide' : 'drawer.fourColorShow')
    },
    {
      route: DrawerRoutes.sound,
      title: translate('drawer.sounds')
    },
    {
      route: DrawerRoutes.support,
      title: translate('drawer.support'),
      sectionBreak: true
    }
  ]

  let controls = TopUserControls
  if (isInGame) {
    controls = controls.concat(TopGameControls)
  } else {
    controls = controls.concat([
      {
        route: DrawerRoutes.about,
        title: translate('drawer.about')
      },
      {
        route: DrawerRoutes.signup, // Book, enroll
        title: translate('drawer.book'),
        svg: <LinkArrow />
      }
    ])
    controls.push({
      route: DrawerRoutes.shop,
      title: translate('drawer.shop'),
      svg: <LinkArrow />
    })
  }

  return controls
}

const getControlsOutOfGame = ({ isAdmin }: { isAdmin: boolean }): IDrawerItem[] => {
  const adminRoutes: IDrawerItem[] = [
    {
      route: DrawerRoutes.admin,
      title: translate('drawer.admin'),
      svg: <MaterialIcons name="admin-panel-settings" size={18} color="black" />
    },
    {
      route: DrawerRoutes.courses,
      title: 'Courses',
      svg: <MaterialIcons name="school" size={18} color="black" />
    }
  ]
  const routes: IDrawerItem[] = [
    {
      route: DrawerRoutes.privacyPolicy,
      title: translate('drawer.privacy')
    },
    {
      route: DrawerRoutes.termsOfService,
      title: translate('drawer.terms')
    },
    {
      route: DrawerRoutes.support,
      title: translate('drawer.support')
    }
  ]

  if (ENABLE_NOTIFICATIONS) {
    routes.unshift({
      route: DrawerRoutes.notifications,
      title: translate('drawer.notifications')
    })
  }

  routes.push({
    route: DrawerRoutes.logout,
    title: translate('drawer.logout'),
    emphasize: true,
    sectionBreak: true
  })

  // const loungesRoute: IDrawerItem[] = [
  //   {
  //     route: DrawerRoutes.lounges,
  //     title: 'Lounges'
  //   }
  // ]

  return [...routes, ...(isAdmin ? adminRoutes : [])]
}

export const DrawerContent = observer(() => {
  const router = useRouter()
  const { open: openBottomModal } = useCheatSheet()
  const { open: openSupportFormBottomModal } = useSupportFormModal()
  const { bottomNotificationSheetModalRef, handleOpenNotificationSheet } = useNotificationSheet()
  const store = useStore()
  const {
    memberships: { getMySubscription, isSubscribed }
  } = useStore()

  const { isInGame, isMyFirstGame } = useIsInGame()
  const isEmbedded = useIsEmbeddedSegment()

  const isAdmin = store.session?.isAdmin || store.session?.isSuperAdmin
  const isDrawerOpen = useDrawerStore().status === 'open'

  const isCommunityAccessEnabled = useRemoteConfig.getState().is_community_enabled

  const isFourColoredDeckEnabled = store.game?.isFourColoredDeckEnabled
  const isSoundEnabled = store.permissions?.isSoundEnabled
  let tournamentName = store?.game.tournamentName

  const gameId = store.game?.game?.gameId
  const shouldShowMembershipButtons = !(gameId || isMyFirstGame)
  const controlsOutOfGame = getControlsOutOfGame({ isAdmin })
  const drawerItems = isMyFirstGame
    ? []
    : getDrawerItems({
        isInGame,
        isFourColoredDeckEnabled,
        isSubscribed,
        shouldShowMembershipButtons,
        isCommunityAccessEnabled,
        isAdmin
      })

  const memberShipScreenEnabled = useRemoteConfig((state) => state.tab_nav_config.enabledTabs.membershipScreen)
  const showMembershipCta = memberShipScreenEnabled && !isSubscribed && shouldShowMembershipButtons

  useFocusEffect(
    useCallback(() => {
      !isEmbedded && getMySubscription()
    }, [getMySubscription, isEmbedded])
  )

  if (store?.game?.currentGameWithTournament?.accessControl === 'community') {
    const date = new Date(store?.game?.currentGameWithTournament?.startTime)
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })

    const formattedTime = formatter.format(date)
    tournamentName = `${tournamentName} (${formattedTime})`
  }

  const onLogout = async () => {
    try {
      toggleDrawer()
      await store.game.leaveCurrentGame()
    } catch (error) {
      logger.error('leaveCurrentGame', error)
    } finally {
      store.session.logout()
      isWeb && broadcastChannel?.postMessage({ type: 'logout' })
      isAndroid && router.navigate('/login')
    }
  }

  const gotoAbout = async () => {
    toggleDrawer()
    router.navigate('/about')
  }

  const toggleDrawer = () => {
    useDrawerStore.getState().closeDrawer()
  }

  const openSupportModal = () => {
    if (isInGame) {
      openSupportFormBottomModal()
    } else {
      router.navigate('/support')
    }
    toggleDrawer()
  }

  const onOpenLessons = () => {
    const url = 'https://www.pokerpower.com/beginner-lessons'
    toggleDrawer()
    Linking.openURL(url)
  }

  const onOpenStore = () => {
    const url = 'https://store.pokerpower.com/'
    store.analytics.setEvent(events.sideNav.shop_link_tapped)
    toggleDrawer()
    Linking.openURL(url)
  }

  const onPolicy = () => {
    toggleDrawer()
    router.navigate('/privacy-policy')
  }
  const onTermOfService = () => {
    toggleDrawer()
    router.navigate('/terms-of-service')
  }

  const toggleSound = () => store.permissions.setIsSoundEnabled(!isSoundEnabled)

  const onOpenCheatSheet = useThrottledCallback(() => {
    openBottomModal()
  }, 250)

  const onOpenNotificationSheet = useThrottledCallback(() => {
    handleOpenNotificationSheet()
  }, 1000)

  const navigateToPremiumAndTrack = () => {
    store.analytics.setEvent('CTA Go Premium Tapped', {
      segmentUserId: store.analytics.getSegmentUserID(),
      segmentIdentity: store.analytics.getUserIdentify(),
      from_screen: 'Profile'
    })
    router.navigate('/(root)/(tabs)/membership')
  }

  const onOpenLeaveGameSheet = useThrottledCallback(() => {
    const isCommunityGame = store.game.currentTournament?.accessControl === 'community'
    const notFinished = store.game.game?.gameState?.tag !== 'finished'
    const notObserver =
      !store.game.game ||
      !!(store.game.game?.players as Player[]).find(
        (player) => player.userId === store.session.user?._id && player.state.tag !== 'observer'
      )

    store.game.createLeaveGameSheet(isCommunityGame && notFinished && notObserver)
  }, 100)

  const onOpenLeaveFirstGameSheet = useThrottledCallback(() => {
    store.game.createLeaveFirstGameSheet()
  }, 100)

  const useFourColor = store.game.isFourColoredDeckEnabled

  const onPressDrawerItem = (e: GestureResponderEvent, item: IDrawerItem) => {
    if (isDrawerOpen) {
      e.preventDefault()
      switch (item.route) {
        case DrawerRoutes.about:
          gotoAbout()
          break

        case DrawerRoutes.admin:
          toggleDrawer()
          router.navigate('/(root)/admin')
          break

        case DrawerRoutes.signup: // Book, enroll
          onOpenLessons()
          break

        case DrawerRoutes.support:
          openSupportModal()
          break

        case DrawerRoutes.leaderboard:
          store.game.setIsLeaderboardAccessedFromDrawer(true)
          toggleDrawer()
          router.navigate('/game/leaderboard')
          break

        case DrawerRoutes.cheatSheet:
          onOpenCheatSheet()
          toggleDrawer()
          break

        case DrawerRoutes.logout:
          onLogout()
          break

        case DrawerRoutes.membership:
          router.navigate('/lobby/membership')
          break

        case DrawerRoutes.privacyPolicy:
          onPolicy()
          break

        case DrawerRoutes.termsOfService:
          onTermOfService()
          break

        case DrawerRoutes.shop:
          onOpenStore()
          break

        case DrawerRoutes.courses:
          toggleDrawer()
          router.navigate('/(root)/courses' as Href)
          break

        case DrawerRoutes.lounges:
          toggleDrawer()
          router.navigate('/(root)/lounges')
          break

        case DrawerRoutes.notifications:
          toggleDrawer()
          setTimeout(() => {
            onOpenNotificationSheet()
          }, 25)
          break
      }
    } else {
      toggleDrawer()
      logger.warn('Tried to press item in drawer while drawer is closed..')
    }
  }

  return (
    <>
      <Pressable onPress={toggleDrawer} style={styles.drawerContainer}>
        <View style={[styles.drawerMenuContainer, { paddingTop: isAndroid ? 25 : 0 }]}>
          <SafeAreaView style={{ backgroundColor: 'transparent', flex: 1, justifyContent: 'center' }}>
            <DrawerHeaderItems />

            {drawerItems.length > 0 && (
              <View style={styles.drawerItemsContainer}>
                {showMembershipCta && (
                  <TouchableOpacity onPress={() => navigateToPremiumAndTrack()} style={styles.joinPremiumCta}>
                    <DS.Text variant="Body Medium/Regular">Join Premium</DS.Text>
                    <Crown />
                  </TouchableOpacity>
                )}
                {drawerItems.map((item) => {
                  const state: IDrawerItemState = {
                    isAdmin,
                    isCommunityAccessEnabled,
                    isFourColoredDeckEnabled,
                    isInGame,
                    isSubscribed,
                    shouldShowMembershipButtons,
                    store
                  }
                  if (item.skipItem?.(item, state)) {
                    return null // TODO return without value?
                  }

                  if ([DrawerRoutes.show4ColorDeck, DrawerRoutes.hide4ColorDeck].includes(item.route)) {
                    return <DrawerCardColors key={item.title} useFourColor={useFourColor} />
                  }

                  if (
                    //(store.game?.isTournamentModeOn === false && item.route === DrawerRoutes.leaderboard) ||
                    store.game?.defaultTable?.state.tag === GameStates.ReadyToPlay.tag &&
                    store.game?.isTournamentModeOn === true &&
                    item.route === DrawerRoutes.leaderboard
                  ) {
                    return null
                  }

                  if (item.route === DrawerRoutes.sound) {
                    return (
                      <DrawerItem key={item.title} text={item.title}>
                        <Switch
                          onValueChange={() => toggleSound()}
                          style={{ marginLeft: 16 }}
                          value={isSoundEnabled}
                          color={color.palette.maroon}
                        />
                      </DrawerItem>
                    )
                  }

                  return (
                    <View key={`${item.title}_view`} style={item.sectionBreak ? styles.drawerSectionBreak : undefined}>
                      <DrawerItem key={item.title} text={item.title} onPress={(e) => onPressDrawerItem(e, item)}>
                        {item.svg}
                      </DrawerItem>
                    </View>
                  )
                })}
              </View>
            )}
            {!isInGame && controlsOutOfGame.length > 0 && (
              <View style={styles.drawerItemsContainer}>
                {controlsOutOfGame.map((item) => (
                  <View key={`${item.title}_view`} style={item.sectionBreak ? styles.drawerSectionBreak : undefined}>
                    <DrawerItem
                      key={item.title}
                      text={item.title}
                      secondary={!item.emphasize}
                      bold={item.emphasize}
                      onPress={(e) => onPressDrawerItem(e, item)}
                    >
                      {item.svg}
                    </DrawerItem>
                  </View>
                ))}
              </View>
            )}
          </SafeAreaView>
          {/* BOTTOM SECTION */}
          {isMyFirstGame && (
            <View style={{ paddingLeft: 24, gap: 8 }}>
              <View
                style={{
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  text={translate('teacherOptions.leaveGame')}
                  size="large"
                  onPress={() => {
                    onOpenLeaveFirstGameSheet()
                    setTimeout(toggleDrawer, 25)
                  }}
                />
              </View>
            </View>
          )}
          {isInGame && !isMyFirstGame && (
            <View style={{ paddingLeft: 24, gap: 8 }}>
              <View
                style={{
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  text={translate('teacherOptions.leaveGame')}
                  size="large"
                  onPress={() => {
                    onOpenLeaveGameSheet()
                    setTimeout(toggleDrawer, 25)
                  }}
                />
              </View>
            </View>
          )}
          <Text style={styles.appVersionText}>{BUILD_VERSION_FORMATTED_TEXT}</Text>
          {/* BOTTOM SECTION */}
        </View>
      </Pressable>
      <NotificationCheatSheet bottomNotificationSheetModalRef={bottomNotificationSheetModalRef} />
    </>
  )
})

const styles = StyleSheet.create({
  drawerContainer: {
    flex: 1,
    zIndex: 1,
    width: '100%',
    alignItems: 'flex-end'
  },
  drawerMenuContainer: {
    zIndex: 2,
    height: '100%',
    backgroundColor: color.palette.cream,
    justifyContent: 'flex-end',
    width: 280,
    maxWidth: Platform.OS === 'web' ? 350 : 'auto',
    minWidth: Platform.OS === 'web' ? 300 : 'auto',
    paddingHorizontal: 24
  },
  imageContainer: {
    height: 25,
    width: 25,
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center'
  },
  appVersionText: {
    fontSize: 12,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    color: 'grey',
    marginTop: 8,
    marginBottom: 40,
    alignSelf: 'flex-end'
  },
  joinPremiumCta: {
    width: 150,
    height: 32,
    alignSelf: 'flex-end',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 48,
    paddingHorizontal: DS.spacing[8],
    gap: DS.spacing[8]
  },
  joinPremiumCtaText: {
    color: 'white',
    fontWeight: 'bold'
  },
  drawerItemsContainer: {
    paddingTop: DS.spacing[20],
    backgroundColor: 'transparent',
    borderTopColor: color.palette.lightGrey,
    borderTopWidth: 1,
    paddingBottom: DS.spacing[20],
    gap: DS.spacing[20]
  },
  drawerSectionBreak: {
    paddingTop: DS.spacing[20],
    borderTopColor: color.palette.lightGrey,
    borderTopWidth: 1
    // paddingBottom: DS.spacing[20],
  }
})
